import type { Transaction } from "@rocicorp/zero";
import { type Context, userFacingError } from "../authorization";
import { dropUsagesLostByGrantRemoval } from "../connectorUsages";
import type { Schema } from "../generatedDrizzleSchema";
import { assertNotPersonal } from "../spaceName";
import { grantsForScope } from "./grantQueries";
import { fetchShareableScope } from "./grantResources";
import type { ShareableScopeKind } from "./permissions";
import { checkServiceAccountGrant } from "./serviceAccountGrantAuthorization";
import {
  applyGrantDiff,
  assertDependentPrincipalsMeetRequirements,
  dedupGrants,
  diffGrants,
  type GrantInput,
  principalsCrossingIntoRequirement,
  type ResolvedCustomRole,
  validateGrants,
} from "./validation";

// The core of setScopeRoleGrants: validate, diff against current grants, run the
// cross-resource-availability rules, and apply. Extracted so approving a
// grant request runs the exact same logic as sharing directly — the only
// difference is who is authorized to trigger it. The escalation guard below
// checks the acting principal (`ctx.principal`, e.g. a service account, falling
// back to `ctx.userId`), which is the reviewer when called from an approval.
export async function applyScopeRoleGrants(opts: {
  tx: Transaction<Schema>;
  ctx: Context;
  scopeId: string;
  scopeKind: ShareableScopeKind;
  grants: readonly GrantInput[];
  customRoles: readonly ResolvedCustomRole[];
}): Promise<void> {
  const { tx, ctx, scopeId, scopeKind, grants, customRoles } = opts;

  if (scopeKind === "space") {
    const space = await fetchShareableScope(
      tx,
      scopeId,
      scopeKind,
      ctx.tenantId
    );
    assertNotPersonal(space, "set grants on");
  } else {
    await fetchShareableScope(tx, scopeId, scopeKind, ctx.tenantId);
  }

  const deduped = dedupGrants(grants);
  const resolvedGrants = validateGrants(deduped, scopeKind, customRoles);

  for (const { input, role } of resolvedGrants) {
    if (input.serviceAccountId) {
      const denial = await checkServiceAccountGrant({
        tx,
        ctx,
        scopeId,
        role,
      });
      if (denial?.reason === "allow_all") {
        throw userFacingError(
          "Cannot grant a service account a role carrying allow_all"
        );
      }
      if (denial?.reason === "missing_permission") {
        throw userFacingError(
          `You cannot grant "${denial.permission}" to a service account because you do not hold it on this scope`
        );
      }
    }
  }

  const allExisting = await tx.run(grantsForScope(scopeId, ctx.tenantId));
  const existing = allExisting.filter(
    (g) => g.userGroupId || g.userId || g.serviceAccountId
  );

  const diff = diffGrants(existing, deduped);

  if (scopeKind === "space") {
    const desiredPrincipals = resolvedGrants.map(({ input, role }) => ({
      userGroupId: input.userGroupId ?? null,
      userId: input.userId ?? null,
      serviceAccountId: input.serviceAccountId ?? null,
      builtinRoleId: role.builtinRoleId,
      customRole: role.customRole,
    }));
    for (const ruleId of [
      "space_uses_connected_apps",
      "space_uses_networks",
      "space_uses_skills",
    ] as const) {
      const elevated = principalsCrossingIntoRequirement(
        ruleId,
        existing,
        desiredPrincipals
      );
      await assertDependentPrincipalsMeetRequirements(
        tx,
        ctx.tenantId,
        ruleId,
        scopeId,
        elevated
      );
    }
  }

  await applyGrantDiff(tx, scopeId, diff, ctx.tenantId);

  // Skills need no removal handling: a space "uses" a skill through grant
  // overlap alone, so revoking a grant leaves nothing behind to disconnect.
  if (scopeKind === "connected_app") {
    await dropUsagesLostByGrantRemoval(
      tx,
      ctx.tenantId,
      scopeId,
      diff.removedGroupIds,
      diff.removedUserIds,
      diff.removedServiceAccountIds
    );
  }
}
